import React, { useState, useEffect } from 'react'
import DashboardLayout from './layout'
import { getUser, updateUser } from '../../services/oneUser'
import {
  deleteMyAnnounceByUserId,
  deleteMyEventByUserId,
  deleteMyEstablishmentByUserId,
  deleteMyWorkByUserId,
  deleteMyLibertinByUserId,
} from '../../services/deleteAllAnnounces'
import { useTranslation } from 'react-i18next'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import MyDate from '../components/MyDate'
import MyInput from '../components/MyInput'
import MySelect from '../components/MySelect'
import MyVatInput from '../components/MyVatInput'
import * as Yup from 'yup'
import {
  FaCalendarAlt,
  FaUser,
  FaEnvelope,
  FaCity,
  FaHome,
  FaKey,
  FaSave,
} from 'react-icons/fa'
import dayjs from 'dayjs'
import citiesData from '../../src/data/cities.json'
import { BsDoorClosedFill } from 'react-icons/bs'
import MyButton from '../components/buttons'
import { IoIosWarning } from 'react-icons/io'
import { statusOptions } from './components/announceForms/options/optionsProfile'
import Modal from '../components/MyModal'
import AuthService from '../../services/login'
import ForgotPasswordModal from '../components/ForgotPasswordModal'
import DeleteAccountButton from '../components/DeleteAccountButton'
import { Loader2, CheckCircle2, PlayCircle } from 'lucide-react'
import MyPhoneNumber from '../components/myPhoneNumber'
import PhoneValidationModal from '../components/PhoneValidationModal'
import PhoneUsedModal from '../components/PhoneUsedModal'
import axios, { AxiosError } from 'axios'
import checkPhoneExists from '../fonctions/checkPhoneExists'
import CookieManager from '../lib/CookiesManager'
import useSettingsValidators from '../validators/settings'
import SubmitResultModal from '../components/SubmitResultModal'
import { smsService } from '../../services/smsService'

interface UserProfileFormProps {
  userId: string
}

interface CityOption {
  label: string
  value: string
}

interface Address {
  street?: string
  number?: string
  city?: string
  zip_code?: string
}

interface FormData {
  firstname: string
  lastname: string
  birthday: string
  nickname: string
  email: string
  tva: string
  status: string[]
  address: Address
  phone: string
  countryCode: string
  isPhoneConfirmed?: boolean
}

interface DeleteProgress {
  announces: 'pending' | 'success' | 'loading'
  events: 'pending' | 'success' | 'loading'
  establishments: 'pending' | 'success' | 'loading'
  works: 'pending' | 'success' | 'loading'
  libertins: 'pending' | 'success' | 'loading'
}

const SettingsPage: React.FC<UserProfileFormProps> = ({ userId }) => {
  const { t } = useTranslation()
  const [formData, setFormData] = useState<FormData>({
    firstname: '',
    lastname: '',
    birthday: '',
    nickname: '',
    email: '',
    tva: '',
    status: [],
    address: { street: '', number: '', city: '', zip_code: '' },
    phone: '',
    countryCode: 'BE',
    isPhoneConfirmed: false,
  })

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [invalidFields, setInvalidFields] = useState<{ [key: string]: string }>(
    {},
  )
  const [, setIsFormModified] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [showGreenMessage, setShowGreenMessage] = useState(false)
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false)
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false)
  const [showPhoneValidation, setShowPhoneValidation] = useState(false)
  const [originalPhone, setOriginalPhone] = useState('')
  const [showResultModal, setShowResultModal] = useState(false)
  const [isPhoneValidating, setIsPhoneValidating] = useState(false)
  const [validationMessage, setValidationMessage] = useState({
    type: '',
    message: '',
  })
  const [submitResult, setSubmitResult] = useState({
    success: false,
    message: '',
  })
  const [, setFormError] = useState('')
  const [isCheckingPhone, setIsCheckingPhone] = useState(false)
  const [isPhoneUsedModalOpen, setIsPhoneUsedModalOpen] = useState(false)
  const [phoneError, setPhoneError] = useState('')
  const settingsSchema = useSettingsValidators(userId, formData.email)
  const [tempFormData, setTempFormData] = useState<FormData | null>(null)
  const [pendingStatusData, setPendingStatusData] = useState<{
    status: string
    confirmed: boolean
  }>({ status: '', confirmed: false })
  const cityOptions: CityOption[] = citiesData.flatMap(province =>
    province.cities.map(city => ({
      label: `${city.city_fr}, ${city.postal_code}`,
      value: `${city.city_fr}, ${city.postal_code}`,
    })),
  )
  const isPhoneNumberValid = (
    phoneNumber: string,
    countryCode: string,
  ): boolean => {
    const patterns: Record<string, RegExp> = {
      BE: /^(?:\+32|0)[1-9]\d{7,8}$/,
      FR: /^(?:\+33|0)[67]\d{8}$/,
      DE: /^(?:\+49|0)[1-9]\d{8,12}$/,
      LU: /^(?:\+352)[0-9]{6,10}$/,
      NL: /^(?:\+31|0)[1-9]\d{8}$/,
    }
    return patterns[countryCode]?.test(phoneNumber) || false
  }
  const areAllOperationsComplete = () => {
    return Object.values(deleteProgress).every(status => status === 'success')
  }
  const [deleteProgress, setDeleteProgress] = useState<DeleteProgress>({
    announces: 'pending',
    events: 'pending',
    establishments: 'pending',
    works: 'pending',
    libertins: 'pending',
  })
  const [isDeleting, setIsDeleting] = useState(false)
  const useDebounce = (value: string, delay: number) => {
    const [debouncedValue, setDebouncedValue] = useState(value)

    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedValue(value)
      }, delay)

      return () => {
        clearTimeout(handler)
      }
    }, [value, delay])

    return debouncedValue
  }

  const debouncedPhone = useDebounce(formData.phone, 500)

  useEffect(() => {
    const validatePhone = async () => {
      if (!debouncedPhone || debouncedPhone === originalPhone) {
        setPhoneError('')
        return
      }
    }

    validatePhone()
  }, [debouncedPhone, originalPhone, userId])
  const getStatusIcon = (status: 'pending' | 'success' | 'loading') => {
    switch (status) {
      case 'loading':
        return <Loader2 className='w-5 h-5 text-redpink animate-spin' />
      case 'success':
        return <CheckCircle2 className='w-5 h-5 text-green-500' />
      default:
        return <PlayCircle className='w-5 h-5 text-gray-400' />
    }
  }
  const handleInitialStatusConfirm = () => {
    setShowGreenMessage(true)
    setPendingStatusData(prev => ({
      ...prev,
      confirmed: false,
    }))
  }

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        if (!AuthService.isAuthenticated()) {
          window.location.href = '/login'
          throw new Error('User is not authenticated')
        }

        const token = AuthService.getToken()
        if (!token) {
          throw new Error('No token found')
        }
        const userData = await getUser(userId, token)

        setOriginalPhone(userData.user.phone || '')

        setFormData({
          firstname: userData.user.firstname || '',
          lastname: userData.user.lastname || '',
          birthday: userData.user.birthday
            ? dayjs(userData.user.birthday).format('DD-MM-YYYY')
            : '',
          nickname: userData.user.nickname || '',
          email: userData.user.email || '',
          tva: userData.user.tva || '',
          status: userData.user.status || '',
          address: {
            street: userData.user.address?.street || '',
            number: userData.user.address?.number || '',
            city: userData.user.address?.city || '',
            zip_code: userData.user.address?.zip_code?.toString() || '',
          },
          phone: userData.user.phone || '',
          countryCode: userData.user.countryCode || 'BE',
          isPhoneConfirmed: userData.user.isPhoneConfirmed || false,
        })
        setIsFormModified(false)
        setLoading(false)
      } catch (err) {
        setError('Failed to fetch user data')
        setLoading(false)
      }
    }

    fetchUserData()
  }, [userId])

  const handleInputChange = (field: string, value: string) => {
    setFormData(prevData => {
      const hasChanged =
        field === 'phone'
          ? value !== originalPhone
          : field.includes('.')
          ? prevData.address[field.split('.')[1] as keyof Address] !== value
          : prevData[field as keyof FormData] !== value

      if (hasChanged) {
        if (['city', 'zip_code', 'street', 'number'].includes(field)) {
          return {
            ...prevData,
            address: { ...prevData.address, [field]: value },
          }
        } else {
          return { ...prevData, [field]: value }
        }
      }

      return prevData
    })

    const currentValue = field.includes('.')
      ? formData.address[field.split('.')[1] as keyof Address]
      : formData[field as keyof FormData]

    if (currentValue !== value) {
      setIsFormModified(true)
    }
  }
  const validatePhoneNumber = (
    phoneNumber: string,
    countryCode: string,
  ): boolean => {
    return true
  }
  const handlePhoneChange = (value: string) => {
    handleInputChange('phone', value)
    const isValid = validatePhoneNumber(value, formData.countryCode)
    if (!isValid) {
      setPhoneError(t('invalid-phone-number'))
    }
  }

  const handleCountryChange = (value: string) => {
    handleInputChange('countryCode', value)
  }

  const resetDeleteStates = () => {
    setDeleteProgress({
      announces: 'pending',
      events: 'pending',
      establishments: 'pending',
      works: 'pending',
      libertins: 'pending',
    })
    setIsDeleting(false)
  }
  const clearLocalStorageItems = () => {
    localStorage.removeItem('myAnnounce')
    localStorage.removeItem('myEstablishment')
    localStorage.removeItem('myJobOffer')
    localStorage.removeItem('myEvent')
  }
  const handleFinalDeletion = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsDeleting(true)
    const token = CookieManager.getToken()

    if (!token) {
      setFormError(t('token-missing'))
      return
    }

    const deleteItem = async (
      type: keyof DeleteProgress,
      deleteFunction: (userId: string, token: string) => Promise<any>,
    ) => {
      setDeleteProgress(prev => ({ ...prev, [type]: 'loading' }))
      try {
        await deleteFunction(userId, token)
      } catch (error) {
        // Erreur silencieuse car non critique
      }
      setDeleteProgress(prev => ({ ...prev, [type]: 'success' }))
    }

    try {
      await deleteItem('announces', deleteMyAnnounceByUserId)
      await new Promise(resolve => setTimeout(resolve, 500))

      await deleteItem('events', deleteMyEventByUserId)
      await new Promise(resolve => setTimeout(resolve, 500))

      await deleteItem('establishments', deleteMyEstablishmentByUserId)
      await new Promise(resolve => setTimeout(resolve, 500))

      await deleteItem('works', deleteMyWorkByUserId)
      await new Promise(resolve => setTimeout(resolve, 500))

      await deleteItem('libertins', deleteMyLibertinByUserId)
      await new Promise(resolve => setTimeout(resolve, 500))

      setFormData(prev => ({
        ...prev,
        status: [pendingStatusData.status],
      }))

      const formattedData = {
        ...formData,
        status: [pendingStatusData.status],
        birthday: dayjs(formData.birthday, 'DD-MM-YYYY').format('YYYY-MM-DD'),
      }

      await updateUser(userId, token, formattedData)

      clearLocalStorageItems()
    } catch (error) {
      setFormError(t('error-updating-status'))
    }
  }
  const handleCityChange = (value: string) => {
    const selectedOption = cityOptions.find(option => option.value === value)
    if (selectedOption) {
      const [cityName, postalCode] = selectedOption.label.split(', ')
      handleInputChange('city', cityName)
      handleInputChange('zip_code', postalCode)
    } else {
      handleInputChange('city', '')
      handleInputChange('zip_code', '')
    }
    setIsFormModified(true)
  }

  const handleStatusChange = (value: string) => {
    setPendingStatusData({ status: value, confirmed: false })
    setIsStatusModalOpen(true)
    setShowGreenMessage(false)
  }

  if (loading) return <div>{t('loading')}...</div>
  if (error) return <div>{error}</div>

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setPhoneError('')
    setFormError('')
    setSaveSuccess(false)

    if (!formData.phone.trim()) {
      const errorMessage = t('phone-is-required')
      setPhoneError(errorMessage)

      setSubmitResult({
        success: false,
        message: errorMessage,
      })

      setShowResultModal(true)
      scrollToPhone()
      return
    }

    try {
      await settingsSchema.validate(formData, { abortEarly: false })

      const token = CookieManager.getToken()
      if (!token) {
        throw new Error('No token found')
      }

      if (!formData.isPhoneConfirmed || formData.phone !== originalPhone) {
        setTempFormData({ ...formData })
        setShowPhoneValidation(true)
        return
      }

      await submitForm(formData)

      setSubmitResult({
        success: true,
        message: t('settings-saved-successfully'),
      })
      setShowResultModal(true)
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const errors: { [key: string]: string } = {}
        err.inner.forEach((error: Yup.ValidationError) => {
          if (error.path) {
            errors[error.path] = error.message
          }
        })
        setInvalidFields(errors)

        setSubmitResult({
          success: false,
          message: t('please-check-form-errors'),
        })
        setShowResultModal(true)
      } else {
        handleError(err)
      }
    }
  }

  const handleError = (error: unknown) => {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<{ message: string }>
      const statusCode = axiosError.response?.status
      const errorMessage = axiosError.response?.data?.message

      if (statusCode === 409) {
        setPhoneError(t('phone-already-in-use'))
        scrollToPhone()
      } else if (statusCode === 500) {
        if (errorMessage?.includes('phone')) {
          setPhoneError(t('phone-already-in-use'))
          scrollToPhone()
        } else {
          setFormError(t('server-error'))
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }
      } else {
        setFormError(t('create-ad.error-posting-profile'))
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    } else {
      setFormError(t('create-ad.error-posting-profile'))
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
    setSaveSuccess(false)
  }

  const scrollToPhone = () => {
    const phoneInput = document.querySelector('[name="phone"]')
    if (phoneInput) {
      phoneInput.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  const submitForm = async (dataToSubmit: FormData) => {
    try {
      const token = CookieManager.getToken()
      if (!token) {
        throw new Error('No token found')
      }

      const formattedData = {
        ...dataToSubmit,
        birthday: dayjs(dataToSubmit.birthday, 'DD-MM-YYYY').format(
          'YYYY-MM-DD',
        ),
      }

      await updateUser(userId, token, formattedData)

      setIsFormModified(false)
      setSaveSuccess(true)
      setPhoneError('')
      setFormError('')

      //setTimeout(() => {
      //  window.location.reload()
      //}, 2000)
    } catch (error) {
      handleError(error)
    }
  }

  const handlePhoneValidationConfirm = async (code: string) => {
    try {
      const verificationResult = await smsService.verifyOTP(
        formData.phone,
        code,
      )

      if (verificationResult.message === 'OTP verified successfully') {
        setValidationMessage({
          type: 'success',
          message: t('phone-validation-success'),
        })

        const updatedFormData = {
          ...formData,
          isPhoneConfirmed: true,
        }

        await submitForm(updatedFormData)
        setShowPhoneValidation(false)
      } else {
        setValidationMessage({
          type: 'error',
          message: t('invalid-verification-code'),
        })
      }
    } catch (err) {
      console.error('Error in handlePhoneValidationConfirm:', err)
      setValidationMessage({
        type: 'error',
        message: t('phone-validation-error'),
      })
      setFormData(prev => ({ ...prev, isPhoneConfirmed: false }))
    }
  }

  const handlePhoneModalClose = () => {
    setFormData(prev => ({
      ...prev,
      phone: originalPhone,
      isPhoneConfirmed: false,
    }))
    window.location.reload()
  }

  return (
    <div>
      <DashboardLayout title='Settings'>
        <form onSubmit={handleSubmit} className='space-y-4 px-5 py-2'>
          <div className='flex justify-center bg-anthracite text-white rounded-md p-2'>
            <span>{t('update-account')}</span>
          </div>
          <div className='space-y-6 bg-grisclair text-sm p-2 rounded-md'>
            {t('phone-is-required')}
          </div>
          <div className='space-y-6'>
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
              <div className='w-full'>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <MyDate
                    label={t('create-ad.birthday-dd-mm-yyyy')}
                    value={formData.birthday}
                    onChange={newValue =>
                      handleInputChange('birthday', newValue)
                    }
                    icon={<FaCalendarAlt />}
                    required={true}
                    isInvalid={!!invalidFields['birthday']}
                    error={invalidFields['birthday']}
                  />
                </LocalizationProvider>
              </div>
              <div className='w-full mt-2 sm:mt-0'>
                <MyVatInput
                  value={formData.tva}
                  onChange={value => handleInputChange('tva', value)}
                  isInvalid={!!invalidFields['tva']}
                  error={invalidFields['tva']}
                />
              </div>
            </div>

            <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
              <div className='w-full'>
                <MyInput
                  label={t('create-ad.nickname')}
                  value={formData.nickname}
                  onChange={value => handleInputChange('nickname', value)}
                  icon={<FaUser />}
                  isInvalid={!!invalidFields['nickname']}
                  required={true}
                  error={invalidFields['nickname']}
                />
              </div>
              <div className='w-full mt-2 sm:mt-0'>
                <MyInput
                  label={t('email')}
                  type='email'
                  required={true}
                  value={formData.email}
                  onChange={value => handleInputChange('email', value)}
                  icon={<FaEnvelope />}
                  isInvalid={!!invalidFields['email']}
                  error={invalidFields['email']}
                />
              </div>
            </div>

            <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
              <div className='w-full'>
                <MyInput
                  label={t('firstname')}
                  value={formData.firstname}
                  onChange={value => handleInputChange('firstname', value)}
                  icon={<FaUser />}
                  isInvalid={!!invalidFields['firstname']}
                  error={invalidFields['firstname']}
                />
              </div>
              <div className='w-full mt-2 sm:mt-0'>
                <MyInput
                  label={t('lastname')}
                  value={formData.lastname}
                  onChange={value => handleInputChange('lastname', value)}
                  icon={<FaUser />}
                  isInvalid={!!invalidFields['lastname']}
                  error={invalidFields['lastname']}
                />
              </div>
            </div>

            <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
              <div className='w-full'>
                <MyInput
                  label={t('street')}
                  value={formData.address.street || ''}
                  onChange={value => handleInputChange('street', value)}
                  icon={<FaHome />}
                  isInvalid={!!invalidFields['street']}
                  error={invalidFields['street']}
                />
              </div>
              <div className='w-full mt-2 sm:mt-0'>
                <MyInput
                  label={t('number')}
                  value={formData.address.number || ''}
                  onChange={value => handleInputChange('number', value)}
                  icon={<BsDoorClosedFill />}
                  isInvalid={!!invalidFields['number']}
                  error={invalidFields['number']}
                />
              </div>
            </div>
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-4 w-full'>
              <div>
                <MySelect
                  label={t('city-postal-code')}
                  value={`${formData.address.city}, ${formData.address.zip_code}`}
                  onChange={handleCityChange}
                  options={cityOptions}
                  icon={<FaCity />}
                  className='h-40px'
                  required={true}
                  isInvalid={
                    'city' in invalidFields || 'zip_code' in invalidFields
                  }
                  error={invalidFields['city'] || invalidFields['zip_code']}
                />
              </div>

              <div className='relative w-full mt-2 sm:mt-0'>
                <MyPhoneNumber
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  onCountryChange={handleCountryChange}
                  countryCode={formData.countryCode}
                  isPhoneConfirmed={formData.isPhoneConfirmed}
                  error={phoneError}
                />
                {isCheckingPhone && (
                  <div className='absolute top-10 w-full text-xs left-1/2 -translate-x-1/2 text-emerald-500 text-center'>
                    {t('checking-phone')}...
                  </div>
                )}
              </div>
            </div>

            <div className='w-full'>
              <MySelect
                label={t('status')}
                value={formData.status[0] || ''}
                onChange={handleStatusChange}
                showNonMatchingOptions={true}
                options={statusOptions.map(option => ({
                  value: option.value,
                  label: t(option.label),
                }))}
                icon={<FaUser />}
                className='h-40px'
                required={true}
                isInvalid={!!invalidFields['status']}
                error={invalidFields['status']}
              />
            </div>
          </div>

          <div className='flex flex-col md:flex-row gap-3 md:gap-4 mt-6'>
            <MyButton
              href='#'
              className='w-full md:w-1/3 min-h-[44px] flex items-center justify-center transition-all'
              variant='anthracite-redpink'
              onClick={(e: { preventDefault: () => void }) => {
                e.preventDefault()
                setIsForgotPasswordOpen(true)
              }}
            >
              <FaKey className='text-lg md:text-xl mr-2' />
              <span>{t('reset-password')}</span>
            </MyButton>

            <DeleteAccountButton
              userId={userId}
              className='w-full md:w-1/3 min-h-[44px] flex items-center justify-center'
            />

            <MyButton
              type='submit'
              variant='green'
              className='w-full md:w-1/3 min-h-[44px] flex items-center justify-center'
              disabled={
                saveSuccess ||
                !isPhoneNumberValid(formData.phone, formData.countryCode) ||
                !!phoneError ||
                isCheckingPhone
              }
            >
              <FaSave className='text-lg md:text-xl mr-2' />
              <span>{t('save')}</span>
            </MyButton>
          </div>
        </form>
      </DashboardLayout>
      <Modal
        isOpen={isStatusModalOpen}
        onClose={() => {
          setIsStatusModalOpen(false)
          setShowGreenMessage(false)
          setPendingStatusData({ status: '', confirmed: false })
          resetDeleteStates()
        }}
      >
        <div>
          <p className='text-lg py-2 text-white bg-anthracite rounded-t-lg px-2'>
            {t('reseting-account')}
          </p>
          <div className='py-4 px-5'>
            {!showGreenMessage ? (
              <>
                <p>
                  <IoIosWarning className='inline mr-2 text-2xl text-redpink' />
                  {t('status-alert')}
                </p>
                <MyButton
                  onClick={handleInitialStatusConfirm}
                  variant='redpink-anthracite'
                  className='h-10 mt-4'
                >
                  {t('i-agree')}
                </MyButton>
              </>
            ) : (
              <>
                {!isDeleting ? (
                  <>
                    <div className='mt-4 mb-4 bg-greenlight p-2 text-sm rounded-md'>
                      <p className='text-anthracite'>
                        {t('are-you-sure-delete')}
                      </p>
                    </div>
                    <div className='flex flex-col sm:flex-row justify-center gap-4'>
                      <MyButton
                        onClick={() => {
                          setIsStatusModalOpen(false)
                          setShowGreenMessage(false)
                          resetDeleteStates()
                        }}
                        variant='anthracite-redpink'
                        className='h-10 w-full sm:w-auto'
                      >
                        {t('cancel')}
                      </MyButton>
                      <MyButton
                        onClick={handleFinalDeletion}
                        variant='redpink-anthracite'
                        className='h-10 w-full sm:w-auto'
                        disabled={isDeleting}
                      >
                        {t('delete')}
                      </MyButton>
                    </div>
                  </>
                ) : (
                  <div className='space-y-3 max-w-md mx-auto'>
                    <div className='rounded-lg p-2 bg-redpink'>
                      <div className='flex items-center space-x-2 text-white'>
                        {areAllOperationsComplete() ? (
                          <>
                            <CheckCircle2 className='w-5 h-5 text-white' />
                            <p className='font-medium'>
                              {t('account-reseted-success')}
                            </p>
                          </>
                        ) : (
                          <>
                            <Loader2 className='w-5 h-5 text-white animate-spin' />
                            <p className='font-medium'>{t('please-wait')}</p>
                          </>
                        )}
                      </div>
                    </div>

                    <div className='bg-white rounded-lg'>
                      <div className='space-y-2'>
                        <div
                          className={`flex items-center text-sm justify-between p-3 rounded-md transition-colors ${
                            deleteProgress.announces === 'success'
                              ? 'bg-grisclair'
                              : deleteProgress.announces === 'loading'
                              ? 'bg-grisclair/30'
                              : 'bg-gray-50'
                          }`}
                        >
                          <span className='font-medium text-anthracite'>
                            {t('announces')}
                          </span>
                          <div className='flex items-center space-x-2'>
                            <span className='text-xl'>
                              {getStatusIcon(deleteProgress.announces)}
                            </span>
                          </div>
                        </div>

                        <div
                          className={`flex items-center text-sm justify-between p-3 rounded-md transition-colors ${
                            deleteProgress.events === 'success'
                              ? 'bg-grisclair'
                              : deleteProgress.events === 'loading'
                              ? 'bg-grisclair/30'
                              : 'bg-gray-50'
                          }`}
                        >
                          <span className='font-medium text-anthracite'>
                            {t('events')}
                          </span>
                          <div className='flex items-center space-x-2'>
                            <span className='text-xl'>
                              {getStatusIcon(deleteProgress.events)}
                            </span>
                          </div>
                        </div>

                        <div
                          className={`flex items-center text-sm justify-between p-3 rounded-md transition-colors ${
                            deleteProgress.establishments === 'success'
                              ? 'bg-grisclair'
                              : deleteProgress.establishments === 'loading'
                              ? 'bg-grisclair/30'
                              : 'bg-gray-50'
                          }`}
                        >
                          <span className='font-medium text-anthracite'>
                            {t('establishments')}
                          </span>
                          <div className='flex items-center space-x-2'>
                            <span className='text-xl'>
                              {getStatusIcon(deleteProgress.establishments)}
                            </span>
                          </div>
                        </div>

                        <div
                          className={`flex items-center text-sm justify-between p-3 rounded-md transition-colors ${
                            deleteProgress.works === 'success'
                              ? 'bg-grisclair'
                              : deleteProgress.works === 'loading'
                              ? 'bg-grisclair/30'
                              : 'bg-gray-50'
                          }`}
                        >
                          <span className='font-medium text-anthracite'>
                            {t('works')}
                          </span>
                          <div className='flex items-center space-x-2'>
                            <span className='text-xl'>
                              {getStatusIcon(deleteProgress.works)}
                            </span>
                          </div>
                        </div>

                        <div
                          className={`flex items-center text-sm justify-between p-3 rounded-md transition-colors ${
                            deleteProgress.libertins === 'success'
                              ? 'bg-grisclair'
                              : deleteProgress.libertins === 'loading'
                              ? 'bg-grisclair/30'
                              : 'bg-gray-50'
                          }`}
                        >
                          <span className='font-medium text-anthracite'>
                            {t('libertins-announces')}
                          </span>
                          <div className='flex items-center space-x-2'>
                            <span className='text-xl'>
                              {getStatusIcon(deleteProgress.libertins)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {areAllOperationsComplete() && (
                      <div className='flex justify-center pt-2'>
                        <MyButton
                          onClick={() => {
                            setIsStatusModalOpen(false)
                            window.location.reload()
                          }}
                          variant='anthracite-redpink'
                          className='h-12 px-8 text-base font-medium shadow-lg hover:shadow-xl transition-all duration-300'
                        >
                          {t('close')}
                        </MyButton>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </Modal>
      <PhoneValidationModal
        userId={userId}
        isOpen={showPhoneValidation}
        onClose={() => {
          setShowPhoneValidation(false)
          setValidationMessage({ type: '', message: '' })
        }}
        onConfirm={handlePhoneValidationConfirm}
        onCancel={handlePhoneModalClose}
        phoneNumber={formData.phone}
        isPhoneConfirmed={formData.isPhoneConfirmed ?? false}
        isValidating={isPhoneValidating}
        validationMessage={validationMessage}
        userData={{
          firstname: formData.firstname,
          lastname: formData.lastname,
          birthday: formData.birthday,
          nickname: formData.nickname,
          email: formData.email,
          tva: formData.tva,
          status: formData.status,
          address: {
            street: formData.address.street,
            number: formData.address.number,
            city: formData.address.city,
            zip_code: formData.address.zip_code,
          },
        }}
      />
      <ForgotPasswordModal
        isOpen={isForgotPasswordOpen}
        onClose={() => setIsForgotPasswordOpen(false)}
        emailLabel={t('actual-email')}
        initialValue={formData.email}
      />
      <PhoneUsedModal
        isOpen={isPhoneUsedModalOpen}
        onClose={() => setIsPhoneUsedModalOpen(false)}
      />
      <SubmitResultModal
        isOpen={showResultModal}
        onClose={() => setShowResultModal(false)}
        isSuccess={submitResult.success}
        message={submitResult.message}
      />
    </div>
  )
}

export default SettingsPage
